import { useState } from 'react';
import { Sparkles, Loader2, BarChart3, Save } from 'lucide-react';
import { useSkillAnalyzer } from '@/hooks/ai';
import { cn } from '@/lib/utils';
import type { AnalyzedSkill } from '@/services/ai';

interface AISkillAnalyzerProps {
  onSave?: (skills: AnalyzedSkill[]) => void;
}

export function AISkillAnalyzer({ onSave }: AISkillAnalyzerProps) {
  const { skills, isLoading, error, analyze, reset } = useSkillAnalyzer();
  const [formData, setFormData] = useState({
    yearsOfExperience: '',
    jobTitle: '',
    technologies: '',
    specialties: '',
    projectSummary: '',
    certifications: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await analyze({
      yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
      jobTitle: formData.jobTitle,
      technologies: formData.technologies.split(',').map(s => s.trim()).filter(Boolean),
      specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      projectSummary: formData.projectSummary || undefined,
      certifications: formData.certifications ? formData.certifications.split(',').map(s => s.trim()).filter(Boolean) : undefined,
    });
  };

  const handleSave = () => {
    if (skills.length > 0 && onSave) {
      onSave(skills);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI スキル分析</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">
              経験年数 <span className="text-destructive">*</span>
            </label>
            <input
              type="number"
              min="0"
              max="50"
              value={formData.yearsOfExperience}
              onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="3"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              役職 <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="フロントエンドエンジニア"
              required
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            使用技術（カンマ区切り） <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={formData.technologies}
            onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="React, TypeScript, Python, AWS, Docker"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">得意分野（カンマ区切り）</label>
          <input
            type="text"
            value={formData.specialties}
            onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="フロントエンド開発, API設計, インフラ構築"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">主なプロジェクト概要</label>
          <textarea
            value={formData.projectSummary}
            onChange={(e) => setFormData({ ...formData, projectSummary: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            rows={3}
            placeholder="Reactを使用したWebアプリ開発、AWS上でのインフラ構築経験など"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">保有資格（カンマ区切り）</label>
          <input
            type="text"
            value={formData.certifications}
            onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="AWS SAA, 基本情報技術者"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground",
              isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/90"
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                分析中...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                スキルを分析
              </>
            )}
          </button>
          {skills.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                <Save className="h-4 w-4" />
                保存
              </button>
              <button
                type="button"
                onClick={reset}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                リセット
              </button>
            </>
          )}
        </div>
      </form>

      {error && (
        <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {skills.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium">分析結果:</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            {skills.map((skill, index) => (
              <div key={index} className="rounded-md bg-secondary p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-medium">{skill.name}</span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                    {skill.category}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${skill.level}%` }}
                  />
                </div>
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  {skill.level}%
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
